import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from './ConfirmDialog';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUpdateQuoteRequestStatus,
  useDeleteQuoteRequest,
  useConvertQuoteRequest,
} from '../api/hooks';
import type { QuoteRequest, QuoteRequestStatus } from '../api/types';

interface RequestDetailDrawerProps {
  readonly request: QuoteRequest;
  readonly getAccessToken: () => Promise<string>;
  readonly onClose: () => void;
  readonly onUpdate: () => void;
}

function requestStatusBadgeClass(status: QuoteRequestStatus): string {
  switch (status) {
    case 'new':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'viewed':
      return 'bg-secondary text-secondary-foreground';
    case 'quoted':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
  }
}

export function RequestDetailDrawer({
  request,
  getAccessToken,
  onClose,
  onUpdate,
}: Readonly<RequestDetailDrawerProps>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateStatus = useUpdateQuoteRequestStatus(getAccessToken);
  const deleteRequest = useDeleteQuoteRequest(getAccessToken);
  const convertRequest = useConvertQuoteRequest(getAccessToken);

  // Auto-mark as viewed on mount when status is 'new'
  // Intentionally only run on mount — no deps
  useEffect(() => {
    if (request.status === 'new') {
      updateStatus.mutate(
        { id: request.id, status: 'viewed' },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quote-requests'] });
            onUpdate();
          },
        },
      );
    }
  }, []);

  const handleConvert = () => {
    convertRequest.mutate(request.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quote-requests'] });
        toast.success(t('requests.convert'));
        onUpdate();
        onClose();
      },
      onError: () => {
        toast.error(t('error.saveFailed'));
      },
    });
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmed = () => {
    deleteRequest.mutate(request.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quote-requests'] });
        toast.success(t('requests.delete'));
        onUpdate();
        onClose();
      },
      onError: () => {
        toast.error(t('error.deleteFailed'));
      },
    });
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        data-testid="drawer-backdrop"
        className="fixed inset-0 z-40 border-none bg-black/50"
        onClick={onClose}
        aria-label={t('quotes.closeDrawer')}
      />

      {/* Drawer */}
      <div
        data-testid="request-detail-drawer"
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[80vh] flex-col rounded-t-2xl border-t border-border bg-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-lg font-semibold">{request.customer_name}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('quotes.closeDrawer')}
            data-testid="close-drawer-btn"
            className="min-h-[44px]"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {/* Status badge */}
          <div className="mb-4">
            <Badge className={requestStatusBadgeClass(request.status)}>
              {t(`requests.${request.status}`)}
            </Badge>
          </div>

          {/* Contact info */}
          <div className="mb-4 space-y-2">
            {request.customer_email && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('clients.email')}
                </p>
                <p className="mt-0.5 text-sm">{request.customer_email}</p>
              </div>
            )}
            {request.customer_phone && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('clients.phone')}
                </p>
                <p className="mt-0.5 text-sm">{request.customer_phone}</p>
              </div>
            )}
            {request.address && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('clients.address')}
                </p>
                <p className="mt-0.5 text-sm">{request.address}</p>
              </div>
            )}
          </div>

          {/* Project description */}
          <div className="mb-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('intake.description')}
            </p>
            <p className="mt-0.5 text-sm">{request.project_description}</p>
          </div>

          {/* Timestamps */}
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>{formatDate(request.created_at)}</p>
          </div>

          {/* Already converted message */}
          {request.status === 'quoted' && (
            <p
              className="mt-4 text-sm text-muted-foreground"
              data-testid="already-converted-message"
            >
              {t('requests.alreadyConverted')}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={deleteRequest.isPending}
            data-testid="delete-request-btn"
            className="gap-1.5"
          >
            {deleteRequest.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {t('requests.delete')}
          </Button>

          {request.status !== 'quoted' && (
            <Button
              variant="default"
              size="sm"
              onClick={handleConvert}
              disabled={convertRequest.isPending}
              data-testid="convert-request-btn"
              className="gap-1.5"
            >
              {convertRequest.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('requests.convert')}
            </Button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('requests.deleteConfirm')}
        description=""
        confirmLabel={t('requests.delete')}
        cancelLabel={t('quotes.cancel')}
        onConfirm={handleDeleteConfirmed}
        variant="destructive"
      />
    </>
  );
}
